import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ScopeReminderCallout from "@/components/ScopeReminderCallout";
import PriceFramingCard from "@/components/PriceFramingCard";
import { formatPriceCents, checkPricingEligibility } from "@/api/aimeansbusiness";
import { useAuth } from "@/context/AuthContext";
import { 
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, 
  AlertTriangle, Plus, Trash2, Building2, Users, 
  Brain, Target, Calendar, Shield, Lock
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Multi-select options
const BUSINESS_MODEL_OPTIONS = [
  { value: "B2B", label: "B2B (Business to Business)" },
  { value: "B2C", label: "B2C (Business to Consumer)" },
  { value: "B2B2C", label: "B2B2C (Business to Business to Consumer)" },
  { value: "SaaS", label: "SaaS (Software as a Service)" },
  { value: "Marketplace", label: "Marketplace" },
  { value: "Subscription", label: "Subscription Model" },
  { value: "Franchise", label: "Franchise" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Professional_Services", label: "Professional Services" },
  { value: "Other", label: "Other" },
];

const OPERATING_FUNCTIONS = [
  { value: "Sales", label: "Sales" },
  { value: "Marketing", label: "Marketing" },
  { value: "Operations", label: "Operations" },
  { value: "Finance", label: "Finance & Accounting" },
  { value: "HR", label: "Human Resources" },
  { value: "IT", label: "IT / Technology" },
  { value: "CustomerService", label: "Customer Service" },
  { value: "RnD", label: "Research & Development" },
  { value: "Legal", label: "Legal & Compliance" },
  { value: "Supply_Chain", label: "Supply Chain / Logistics" },
];

const OBJECTIVES_OPTIONS = [
  { value: "cost_reduction", label: "Reduce operational costs" },
  { value: "efficiency", label: "Improve efficiency / productivity" },
  { value: "customer_experience", label: "Enhance customer experience" },
  { value: "competitive_advantage", label: "Gain competitive advantage" },
  { value: "innovation", label: "Drive innovation" },
  { value: "data_insights", label: "Better data insights / decision-making" },
  { value: "scalability", label: "Enable scalability" },
  { value: "risk_reduction", label: "Reduce risk exposure" },
  { value: "compliance", label: "Improve compliance" },
  { value: "revenue_growth", label: "Increase revenue" },
];

const INDUSTRY_OPTIONS = [
  "Manufacturing", "Retail", "Healthcare", "Professional Services",
  "Financial Services", "Technology", "Construction", "Transportation",
  "Hospitality", "Education", "Real Estate", "Agriculture", "Other"
];

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Engagement state
  const [ser, setSer] = useState(null);
  const [service, setService] = useState(null);
  
  // Pricing eligibility state (gated until evaluation + SER)
  const [pricingEligible, setPricingEligible] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Business Identification
    legal_business_name: "",
    primary_contact_name: "",
    role_title: "",
    email: "",
    website: "",
    
    // Business Context
    industry: "",
    employees_range: "",
    annual_revenue_range: "",
    business_model: [],
    operating_functions: [],
    
    // AI Exposure
    evaluated_tools: "",
    stakeholder_pressure: "",
    fear_falling_behind: "",
    specific_problems: "",
    
    // Objectives & Constraints
    objectives: [],
    budget_sensitivity: "",
    risk_tolerance: "",
    time_horizon: "",
    
    // Interview Planning
    participants: [{ name: "", role: "" }],
    preferred_format: "zoom",
    availability_notes: "",
    recording_consent: false,
  });

  // Load onboarding state on mount
  useEffect(() => {
    const loadOnboardingState = async () => {
      try {
        const response = await fetch(`${API_URL}/api/onboarding/state`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (!data.has_active_engagement) {
          // No active engagement - redirect to pricing
          navigate('/pricing');
          return;
        }
        
        setSer(data.ser);
        setService(data.service);
        
        // Prefill form if we have existing onboarding inputs
        if (data.onboarding_inputs) {
          const inputs = data.onboarding_inputs;
          setFormData(prev => ({
            ...prev,
            // Business Identification
            legal_business_name: inputs.business_identification?.legal_business_name || "",
            primary_contact_name: inputs.business_identification?.primary_contact_name || "",
            role_title: inputs.business_identification?.role_title || "",
            email: inputs.business_identification?.email || "",
            website: inputs.business_identification?.website || "",
            // Business Context
            industry: inputs.business_context?.industry || "",
            employees_range: inputs.business_context?.employees_range || "",
            annual_revenue_range: inputs.business_context?.annual_revenue_range || "",
            business_model: inputs.business_context?.business_model || [],
            operating_functions: inputs.business_context?.operating_functions || [],
            // AI Exposure
            evaluated_tools: inputs.ai_exposure?.evaluated_tools || "",
            stakeholder_pressure: inputs.ai_exposure?.stakeholder_pressure || "",
            fear_falling_behind: inputs.ai_exposure?.fear_falling_behind || "",
            specific_problems: inputs.ai_exposure?.specific_problems || "",
            // Objectives
            objectives: inputs.objectives_constraints?.objectives || [],
            budget_sensitivity: inputs.objectives_constraints?.budget_sensitivity || "",
            risk_tolerance: inputs.objectives_constraints?.risk_tolerance || "",
            time_horizon: inputs.objectives_constraints?.time_horizon || "",
            // Interview
            participants: inputs.interview_planning?.participants?.length > 0 
              ? inputs.interview_planning.participants 
              : [{ name: "", role: "" }],
            preferred_format: inputs.interview_planning?.preferred_format || "zoom",
            availability_notes: inputs.interview_planning?.availability_notes || "",
            recording_consent: inputs.interview_planning?.recording_consent || false,
          }));
        }
        
        // Check pricing eligibility (requires evaluation + SER)
        try {
          const eligibilityRes = await checkPricingEligibility();
          setPricingEligible(eligibilityRes.pricing_eligible);
        } catch (eligErr) {
          console.error("Failed to check pricing eligibility:", eligErr);
          setPricingEligible(false);
        }
        
      } catch (err) {
        console.error("Failed to load onboarding state:", err);
        setError("Failed to load onboarding. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadOnboardingState();
    }
  }, [token, navigate]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const addParticipant = () => {
    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, { name: "", role: "" }]
    }));
  };

  const removeParticipant = (index) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index)
    }));
  };

  const updateParticipant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const payload = {
        ser_id: ser.id,
        service_id: ser.service_id,
        business_identification: {
          legal_business_name: formData.legal_business_name,
          primary_contact_name: formData.primary_contact_name,
          role_title: formData.role_title,
          email: formData.email,
          website: formData.website || null,
        },
        business_context: {
          industry: formData.industry,
          employees_range: formData.employees_range,
          annual_revenue_range: formData.annual_revenue_range || null,
          business_model: formData.business_model,
          operating_functions: formData.operating_functions,
        },
        ai_exposure: {
          evaluated_tools: formData.evaluated_tools,
          stakeholder_pressure: formData.stakeholder_pressure,
          fear_falling_behind: formData.fear_falling_behind,
          specific_problems: formData.specific_problems || null,
        },
        objectives_constraints: {
          objectives: formData.objectives,
          budget_sensitivity: formData.budget_sensitivity,
          risk_tolerance: formData.risk_tolerance,
          time_horizon: formData.time_horizon,
        },
        interview_planning: {
          participants: formData.participants.filter(p => p.name && p.role),
          preferred_format: formData.preferred_format,
          availability_notes: formData.availability_notes || null,
          recording_consent: formData.recording_consent,
        },
      };
      
      const response = await fetch(`${API_URL}/api/onboarding/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const errorMsg = data?.detail?.error?.message || data?.detail || "Submission failed";
        throw new Error(errorMsg);
      }
      
      // Success - navigate to schedule
      navigate('/schedule');
      
    } catch (err) {
      console.error("Onboarding submission failed:", err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { id: 'confirm', title: 'Service Confirmation', icon: CheckCircle2 },
    { id: 'business', title: 'Business Identification', icon: Building2 },
    { id: 'context', title: 'Business Context', icon: Users },
    { id: 'ai', title: 'AI Exposure', icon: Brain },
    { id: 'objectives', title: 'Objectives & Constraints', icon: Target },
    { id: 'interview', title: 'Interview & Consent', icon: Calendar },
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true; // Service confirmation
      case 1: // Business Identification
        return formData.legal_business_name && formData.primary_contact_name && 
               formData.role_title && formData.email;
      case 2: // Business Context
        return formData.industry && formData.employees_range;
      case 3: // AI Exposure
        return formData.evaluated_tools && formData.stakeholder_pressure && 
               formData.fear_falling_behind;
      case 4: // Objectives
        return formData.budget_sensitivity && formData.risk_tolerance && 
               formData.time_horizon;
      case 5: // Interview - HARD GATE on consent and participants
        const validParticipants = formData.participants.filter(p => p.name && p.role);
        return formData.recording_consent && validParticipants.length >= 1 && 
               formData.preferred_format;
      default: return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <span className="ml-3 text-slate-400">Loading onboarding...</span>
      </div>
    );
  }

  if (!ser || !service) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-300 mb-4">No active engagement found.</p>
          <Button onClick={() => navigate('/pricing')}>
            Select a Service
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Service Onboarding
          </h1>
          <p className="text-slate-400">
            {service.name}
            {!pricingEligible && (
              <span className="inline-flex items-center gap-1 ml-2 text-xs text-slate-500">
                <Lock className="w-3 h-3" />
                Pricing revealed after evaluation
              </span>
            )}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-12 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-700 -z-10" />
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <div 
                key={step.id}
                className="flex flex-col items-center"
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isActive ? 'bg-primary text-white' :
                    isCompleted ? 'bg-green-600 text-white' :
                    'bg-slate-700 text-slate-400'
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 hidden md:block ${isActive ? 'text-primary' : 'text-slate-500'}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500/30 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Step Content */}
        <div className="glass p-8 rounded-2xl mb-8">
          {/* Step 0: Service Confirmation */}
          {currentStep === 0 && (
            <div className="space-y-6" data-testid="onboarding-step-confirm">
              <div className="text-center mb-6">
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  Confirm Your Service Selection
                </h2>
                <p className="text-slate-400">Review the details below before proceeding</p>
              </div>

              {/* Price Framing - GATED (only show when eligible) */}
              {pricingEligible && service.fixed_price_cents ? (
                <PriceFramingCard
                  serviceName={service.name}
                  headline={service.headline}
                  priceCents={service.fixed_price_cents}
                  scopeIncludes={service.scope_includes}
                  scopeExcludes={service.scope_excludes}
                  whatThisPrevents={service.what_this_prevents}
                  positioningStatement={service.positioning_statement}
                />
              ) : (
                <>
                  {/* Show service details without pricing */}
                  <div className="text-center mb-4">
                    <h3 className="font-heading text-xl font-semibold text-white">
                      {service.name}
                    </h3>
                    <p className="text-primary">{service.headline}</p>
                    <p className="text-slate-400 mt-2 text-sm">{service.subhead}</p>
                  </div>

                  <div className="border-t border-slate-700 pt-6">
                    <h3 className="font-semibold text-white mb-4">What&apos;s Included:</h3>
                    <ul className="space-y-2">
                      {service.scope_includes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <ScopeReminderCallout excludes={service.scope_excludes} />
                  
                  {/* Pricing Gate Message */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
                      <Lock className="w-4 h-4" />
                      <span>Pricing revealed after your Evaluation Report</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 1: Business Identification */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="onboarding-step-business">
              <h2 className="font-heading text-xl font-bold text-white mb-4">
                Business Identification
              </h2>
              
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="legal_business_name">Legal Business Name *</Label>
                  <Input
                    id="legal_business_name"
                    value={formData.legal_business_name}
                    onChange={(e) => updateField('legal_business_name', e.target.value)}
                    placeholder="Acme Corporation"
                    className="mt-1"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primary_contact_name">Primary Contact Name *</Label>
                    <Input
                      id="primary_contact_name"
                      value={formData.primary_contact_name}
                      onChange={(e) => updateField('primary_contact_name', e.target.value)}
                      placeholder="John Smith"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role_title">Role / Title *</Label>
                    <Input
                      id="role_title"
                      value={formData.role_title}
                      onChange={(e) => updateField('role_title', e.target.value)}
                      placeholder="CEO, Operations Director, etc."
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="john@acme.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Website (Optional)</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => updateField('website', e.target.value)}
                      placeholder="https://www.acme.com"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Business Context */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="onboarding-step-context">
              <h2 className="font-heading text-xl font-bold text-white mb-4">
                Business Context
              </h2>
              
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Industry *</Label>
                    <Select value={formData.industry} onValueChange={(v) => updateField('industry', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRY_OPTIONS.map(ind => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Number of Employees *</Label>
                    <Select value={formData.employees_range} onValueChange={(v) => updateField('employees_range', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="11-50">11-50</SelectItem>
                        <SelectItem value="51-200">51-200</SelectItem>
                        <SelectItem value="201-500">201-500</SelectItem>
                        <SelectItem value="500+">500+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Annual Revenue Range (Optional)</Label>
                  <Select value={formData.annual_revenue_range} onValueChange={(v) => updateField('annual_revenue_range', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Prefer not to say" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under_1m">Under $1M</SelectItem>
                      <SelectItem value="1m_5m">$1M - $5M</SelectItem>
                      <SelectItem value="5m_20m">$5M - $20M</SelectItem>
                      <SelectItem value="20m_50m">$20M - $50M</SelectItem>
                      <SelectItem value="50m+">$50M+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="mb-3 block">Business Model (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {BUSINESS_MODEL_OPTIONS.map(opt => (
                      <label 
                        key={opt.value}
                        className="flex items-center gap-2 p-2 rounded-lg border border-slate-700 hover:border-primary/50 cursor-pointer text-sm"
                      >
                        <Checkbox 
                          checked={formData.business_model.includes(opt.value)}
                          onCheckedChange={() => toggleArrayField('business_model', opt.value)}
                        />
                        <span className="text-slate-300">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="mb-3 block">Operating Functions (Select primary functions)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {OPERATING_FUNCTIONS.map(opt => (
                      <label 
                        key={opt.value}
                        className="flex items-center gap-2 p-2 rounded-lg border border-slate-700 hover:border-primary/50 cursor-pointer text-sm"
                      >
                        <Checkbox 
                          checked={formData.operating_functions.includes(opt.value)}
                          onCheckedChange={() => toggleArrayField('operating_functions', opt.value)}
                        />
                        <span className="text-slate-300">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: AI Exposure */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="onboarding-step-ai">
              <h2 className="font-heading text-xl font-bold text-white mb-4">
                AI Exposure & Pressure
              </h2>
              
              <div className="grid gap-6">
                <div>
                  <Label>Have you evaluated AI tools for your business? *</Label>
                  <Select value={formData.evaluated_tools} onValueChange={(v) => updateField('evaluated_tools', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select an answer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, actively researching</SelectItem>
                      <SelectItem value="no">No, haven't started</SelectItem>
                      <SelectItem value="unsure">Unsure / Just beginning to think about it</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Level of stakeholder pressure to adopt AI? *</Label>
                  <Select value={formData.stakeholder_pressure} onValueChange={(v) => updateField('stakeholder_pressure', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None - No pressure</SelectItem>
                      <SelectItem value="mild">Mild - Occasional mentions</SelectItem>
                      <SelectItem value="moderate">Moderate - Regular discussions</SelectItem>
                      <SelectItem value="significant">Significant - Urgent priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Fear of falling behind competitors? *</Label>
                  <Select value={formData.fear_falling_behind} onValueChange={(v) => updateField('fear_falling_behind', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None - Not concerned</SelectItem>
                      <SelectItem value="mild">Mild - Some awareness</SelectItem>
                      <SelectItem value="moderate">Moderate - Growing concern</SelectItem>
                      <SelectItem value="significant">Significant - Major concern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="specific_problems">
                    Specific problems you're hoping AI might solve? (Optional)
                  </Label>
                  <Textarea
                    id="specific_problems"
                    value={formData.specific_problems}
                    onChange={(e) => updateField('specific_problems', e.target.value)}
                    placeholder="Describe any specific challenges or opportunities you have in mind..."
                    className="mt-1 min-h-[100px]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Objectives & Constraints */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="onboarding-step-objectives">
              <h2 className="font-heading text-xl font-bold text-white mb-4">
                Objectives & Constraints
              </h2>
              
              <div className="grid gap-6">
                <div>
                  <Label className="mb-3 block">Primary Objectives (Select all that apply)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {OBJECTIVES_OPTIONS.map(opt => (
                      <label 
                        key={opt.value}
                        className="flex items-center gap-2 p-2 rounded-lg border border-slate-700 hover:border-primary/50 cursor-pointer text-sm"
                      >
                        <Checkbox 
                          checked={formData.objectives.includes(opt.value)}
                          onCheckedChange={() => toggleArrayField('objectives', opt.value)}
                        />
                        <span className="text-slate-300">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Budget Sensitivity *</Label>
                    <Select value={formData.budget_sensitivity} onValueChange={(v) => updateField('budget_sensitivity', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - Flexible budget</SelectItem>
                        <SelectItem value="moderate">Moderate - Some constraints</SelectItem>
                        <SelectItem value="high">High - Tight budget</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Risk Tolerance *</Label>
                    <Select value={formData.risk_tolerance} onValueChange={(v) => updateField('risk_tolerance', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - Very cautious</SelectItem>
                        <SelectItem value="moderate">Moderate - Balanced</SelectItem>
                        <SelectItem value="high">High - Open to experimentation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Decision Time Horizon *</Label>
                    <Select value={formData.time_horizon} onValueChange={(v) => updateField('time_horizon', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-6">0-6 months</SelectItem>
                        <SelectItem value="6-18">6-18 months</SelectItem>
                        <SelectItem value="18+">18+ months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Interview Planning & Consent */}
          {currentStep === 5 && (
            <div className="space-y-6" data-testid="onboarding-step-interview">
              <h2 className="font-heading text-xl font-bold text-white mb-4">
                Interview Participation & Consent
              </h2>
              
              <div className="grid gap-6">
                {/* Participants */}
                <div>
                  <Label className="mb-3 block">Interview Participants *</Label>
                  <p className="text-slate-400 text-sm mb-3">
                    At least one participant is required. Add key stakeholders who will participate in the structured interview.
                  </p>
                  <div className="space-y-3">
                    {formData.participants.map((p, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Name"
                            value={p.name}
                            onChange={(e) => updateParticipant(index, 'name', e.target.value)}
                          />
                          <Input
                            placeholder="Role"
                            value={p.role}
                            onChange={(e) => updateParticipant(index, 'role', e.target.value)}
                          />
                        </div>
                        {formData.participants.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeParticipant(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addParticipant}
                      className="mt-2"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Participant
                    </Button>
                  </div>
                </div>
                
                {/* Meeting Format */}
                <div>
                  <Label>Preferred Meeting Format *</Label>
                  <Select value={formData.preferred_format} onValueChange={(v) => updateField('preferred_format', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoom">Zoom (Video Conference)</SelectItem>
                      <SelectItem value="in_person">In-Person Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Availability Notes */}
                <div>
                  <Label htmlFor="availability_notes">Scheduling Notes (Optional)</Label>
                  <Textarea
                    id="availability_notes"
                    value={formData.availability_notes}
                    onChange={(e) => updateField('availability_notes', e.target.value)}
                    placeholder="Any scheduling preferences, time zones, or constraints..."
                    className="mt-1"
                  />
                </div>
                
                {/* Recording Consent - HARD GATE */}
                <div className="border border-amber-500/30 bg-amber-950/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-2">Recording Consent Required</h4>
                      <p className="text-slate-400 text-sm mb-4">
                        Our structured interviews are recorded for quality assurance and to ensure accurate 
                        documentation. Recordings are:
                      </p>
                      <ul className="text-slate-400 text-sm space-y-1 mb-4">
                        <li>• Time-boxed to the scheduled session duration</li>
                        <li>• Used solely for engagement documentation</li>
                        <li>• Stored securely and confidentially</li>
                        <li>• Never shared with third parties</li>
                      </ul>
                      
                      <label 
                        className="flex items-start gap-3 cursor-pointer"
                        data-testid="recording-consent-checkbox"
                      >
                        <Checkbox 
                          checked={formData.recording_consent}
                          onCheckedChange={(checked) => updateField('recording_consent', checked)}
                          className="mt-0.5"
                        />
                        <span className="text-white text-sm">
                          I consent to the recording of structured interview sessions for the purposes 
                          described above. I understand this consent is required to proceed.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Validation Warning */}
                {!canProceed() && (
                  <div className="flex items-center gap-2 text-amber-400 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    <span>
                      Please ensure you have at least one participant with name and role, 
                      and have acknowledged the recording consent.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="bg-primary hover:bg-primary/90"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className="bg-primary hover:bg-primary/90"
              data-testid="onboarding-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Complete Onboarding
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
